if (!customElements.get('delivery-estimate')) {
  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        this.estimateEl = this.querySelector('.delivery-estimate__estimate');
        this.countdownEl = this.querySelector('.delivery-estimate__countdown');
        this.holidays = new Set(this.dataset.holidays.match(/\d{4}-\d{2}-\d{2}/g) || []);
        this.dateFormat = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        });

        this.render();
        this.interval = setInterval(() => this.render(), 60000);

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.section) return;
            this.classList.toggle('hidden', data.variant ? !data.variant.available : false);
          });
        }
      }

      disconnectedCallback() {
        clearInterval(this.interval);
        this.variantChangeUnsubscriber?.();
      }

      // Current calendar date (as a UTC-midnight Date) and minutes since midnight in the shop timezone.
      getShopNow() {
        let parts;
        try {
          parts = new Intl.DateTimeFormat('en-US', {
            timeZone: this.dataset.timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
          }).formatToParts(new Date());
        } catch (error) {
          parts = new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
          }).formatToParts(new Date());
        }
        const get = (type) => parseInt(parts.find((part) => part.type === type).value);

        return {
          date: new Date(Date.UTC(get('year'), get('month') - 1, get('day'))),
          minutes: get('hour') * 60 + get('minute'),
        };
      }

      isBusinessDay(date) {
        const day = date.getUTCDay();
        const isWeekend = day === 0 || day === 6;
        if (isWeekend && this.dataset.weekendShipping !== 'true') return false;
        return !this.holidays.has(date.toISOString().slice(0, 10));
      }

      addDays(date, days) {
        const next = new Date(date);
        next.setUTCDate(next.getUTCDate() + days);
        return next;
      }

      addBusinessDays(date, days) {
        let result = new Date(date);
        let remaining = days;
        while (remaining > 0) {
          result = this.addDays(result, 1);
          if (this.isBusinessDay(result)) remaining--;
        }
        return result;
      }

      formatCountdown(totalMinutes) {
        const days = Math.floor(totalMinutes / 1440);
        const hours = Math.floor((totalMinutes % 1440) / 60);
        const minutes = totalMinutes % 60;
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
      }

      fill(template, values) {
        return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, value), template);
      }

      render() {
        const now = this.getShopNow();
        const cutoffMinutes = parseInt(this.dataset.cutoffHour) * 60;
        const processingMin = parseInt(this.dataset.processingMin) || 0;
        const processingMax = Math.max(parseInt(this.dataset.processingMax) || 0, processingMin);
        const transitMin = parseInt(this.dataset.transitMin) || 0;
        const transitMax = Math.max(parseInt(this.dataset.transitMax) || 0, transitMin);

        // Orders placed before the cutoff on a business day start processing today, otherwise the next business day.
        let offset = 0;
        let startDate = now.date;
        if (!this.isBusinessDay(startDate) || now.minutes >= cutoffMinutes) {
          do {
            offset++;
            startDate = this.addDays(now.date, offset);
          } while (!this.isBusinessDay(startDate));
        }

        const dispatchDate = this.addBusinessDays(startDate, processingMax);
        const values = {
          start: this.dateFormat.format(this.addBusinessDays(startDate, processingMin + transitMin)),
          end: this.dateFormat.format(this.addBusinessDays(startDate, processingMax + transitMax)),
          dispatch: this.dateFormat.format(dispatchDate),
          countdown: this.formatCountdown(offset * 1440 + cutoffMinutes - now.minutes),
        };

        this.estimateEl.innerHTML = this.fill(this.estimateEl.dataset.template, values);
        if (this.countdownEl) {
          this.countdownEl.innerHTML = this.fill(this.countdownEl.dataset.template, values);
          this.countdownEl.classList.remove('hidden');
        }
      }
    }
  );
}
